import { useState, useEffect } from "react";
import { useAuth, UserButton } from "@clerk/clerk-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { client } from "@/client";
import { formatDateTime, formatTimeOnly, localToUtcIso, utcIsoToLocal } from "@/lib/format-time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, QrCode, Users, CalendarDays, Loader2, Download, Trash2, Pencil } from "lucide-react";
import QRCode from "react-qr-code";
import { DeletedUsersSection } from "./deleted-users-section";

interface Event {
  id: number;
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime: string;
  points: number;
  createdAt: string;
}

interface QrCodeData {
  id: number;
  token: string;
  eventId: number;
  isActive: boolean;
}

interface Attendee {
  userId: number;
  name: string;
  email: string;
  checkedInAt: string;
  pointsEarned: number;
}

export function AdminDashboard() {
  const { getToken } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [qrCode, setQrCode] = useState<QrCodeData | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // 이벤트 폼
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    startTime: "",
    endTime: "",
    points: 10,
  });

  useEffect(() => {
    fetchEvents();
    fetchUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUserRole() {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await client.api.auth.me.$get({}, { headers });
      const data = (await res.json()) as { user: { role: string } };
      setUserRole(data.user.role);
    } catch (err) {
      console.error("Failed to fetch user role:", err);
    }
  }

  async function fetchEvents() {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await client.api.events.$get({}, { headers });
      const data = (await res.json()) as { events: Event[] };
      setEvents(data.events);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setIsLoading(false);
    }
  }

  async function createEvent() {
    setIsCreating(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      // datetime-local 값을 UTC ISO 문자열로 변환하여 전송
      const payload = {
        ...formData,
        startTime: localToUtcIso(formData.startTime),
        endTime: localToUtcIso(formData.endTime),
      };
      const res = await client.api.events.$post({ json: payload }, { headers });

      if (res.ok) {
        setFormData({
          title: "",
          description: "",
          location: "",
          startTime: "",
          endTime: "",
          points: 10,
        });
        fetchEvents();
      }
    } catch (err) {
      console.error("Failed to create event:", err);
    } finally {
      setIsCreating(false);
    }
  }

  async function viewEventDetails(event: Event) {
    setSelectedEvent(event);
    const token = await getToken();
    const headers = { Authorization: `Bearer ${token}` };

    // QR 코드 조회
    const qrRes = await client.api["qr-codes"].event[":eventId"].$get(
      { param: { eventId: String(event.id) } },
      { headers }
    );
    const qrData = (await qrRes.json()) as { qrCodes: QrCodeData[] };
    setQrCode(qrData.qrCodes?.[0] ?? null);

    // 출석자 명단 조회
    const attendeesRes = await client.api["check-ins"].event[":eventId"].$get(
      { param: { eventId: String(event.id) } },
      { headers }
    );
    const attendeesData = (await attendeesRes.json()) as { attendees: Attendee[] };
    setAttendees(attendeesData.attendees);
  }

  function startEditEvent(event: Event) {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description ?? "",
      location: event.location ?? "",
      startTime: utcIsoToLocal(event.startTime),
      endTime: utcIsoToLocal(event.endTime),
      points: event.points,
    });
  }

  async function updateEvent() {
    if (!editingEvent) return;
    setIsEditing(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        title: formData.title,
        description: formData.description || undefined,
        location: formData.location || undefined,
        startTime: localToUtcIso(formData.startTime),
        endTime: localToUtcIso(formData.endTime),
        points: formData.points,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await (client.api.events[":id"].$patch as any)(
        { param: { id: String(editingEvent.id) }, json: payload },
        { headers }
      );

      if (res.ok) {
        setEditingEvent(null);
        setFormData({
          title: "",
          description: "",
          location: "",
          startTime: "",
          endTime: "",
          points: 10,
        });
        fetchEvents();
      }
    } catch (err) {
      console.error("Failed to update event:", err);
    } finally {
      setIsEditing(false);
    }
  }

  async function deleteEvent(eventId: number) {
    if (!confirm("정말 이 이벤트를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
      return;
    }
    setIsDeleting(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await client.api.events[":id"].$delete(
        { param: { id: String(eventId) } },
        { headers }
      );

      if (res.ok) {
        setSelectedEvent(null);
        fetchEvents();
      }
    } catch (err) {
      console.error("Failed to delete event:", err);
    } finally {
      setIsDeleting(false);
    }
  }

  function downloadQrCode() {
    if (!qrCode) return;

    const svg = document.getElementById("qr-code-svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngUrl = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-${selectedEvent?.title}-${qrCode.token.slice(0, 8)}.png`;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between px-4">
          <div>
            <h1 className="text-xl font-bold">Admin 대시보드</h1>
            <p className="text-xs text-muted-foreground">목요모임 관리</p>
          </div>
          <UserButton />
        </div>
      </header>

      <main className="container px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <CalendarDays className="h-4 w-4" />
                전체 이벤트
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{events.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                이번 주 출석
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">-</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <QrCode className="h-4 w-4" />
                활성 QR
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">-</p>
            </CardContent>
          </Card>
        </div>

        {/* Create Event */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              이벤트 관리
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    새 이벤트
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>새 이벤트 생성</DialogTitle>
                    <DialogDescription>
                      예배/모임 정보를 입력하세요. QR 코드가 자동 생성됩니다.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="title">제목 *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        placeholder="예: 2026년 1월 목요모임"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">설명</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({ ...formData, description: e.target.value })
                        }
                        placeholder="예: 신년 첫 모임"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location">장소</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                        placeholder="예: 필라델피아 교회"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="startTime">시작 시간 *</Label>
                        <Input
                          id="startTime"
                          type="datetime-local"
                          value={formData.startTime}
                          onChange={(e) =>
                            setFormData({ ...formData, startTime: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="endTime">종료 시간 *</Label>
                        <Input
                          id="endTime"
                          type="datetime-local"
                          value={formData.endTime}
                          onChange={(e) =>
                            setFormData({ ...formData, endTime: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="points">포인트</Label>
                      <Input
                        id="points"
                        type="number"
                        value={formData.points}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormData({ ...formData, points: parseInt(e.target.value) || 10 })
                        }
                      />
                    </div>
                    <Button
                      className="w-full"
                      onClick={createEvent}
                      disabled={isCreating || !formData.title || !formData.startTime || !formData.endTime}
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          생성 중...
                        </>
                      ) : (
                        "이벤트 생성"
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>제목</TableHead>
                  <TableHead>시간</TableHead>
                  <TableHead>포인트</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(event.startTime)}
                    </TableCell>
                    <TableCell>{event.points}점</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewEventDetails(event)}
                          >
                            <QrCode className="h-4 w-4 mr-1" />
                            상세
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{selectedEvent?.title}</DialogTitle>
                            <DialogDescription>
                              {formatDateTime(selectedEvent?.startTime ?? "")} -{" "}
                              {formatDateTime(selectedEvent?.endTime ?? "")}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* QR Code */}
                            <div className="flex flex-col items-center space-y-4">
                              <h3 className="font-semibold">QR 코드</h3>
                              {qrCode && (
                                <>
                                  <div className="bg-white p-4 rounded-lg">
                                    <QRCode
                                      id="qr-code-svg"
                                      value={`${window.location.origin}/checkin/${qrCode.token}`}
                                      size={180}
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground text-center break-all">
                                    {window.location.origin}/checkin/{qrCode.token.slice(0, 8)}...
                                  </p>
                                  <Button variant="outline" size="sm" onClick={downloadQrCode}>
                                    <Download className="h-4 w-4 mr-2" />
                                    다운로드
                                  </Button>
                                </>
                              )}
                            </div>
                            {/* Attendees */}
                            <div>
                              <h3 className="font-semibold mb-3">
                                출석자 ({attendees.length}명)
                              </h3>
                              <div className="max-h-60 overflow-y-auto space-y-2">
                                {attendees.map((a) => (
                                  <div
                                    key={a.userId}
                                    className="flex justify-between text-sm p-2 bg-muted rounded"
                                  >
                                    <span>{a.name ?? a.email}</span>
                                    <span className="text-muted-foreground">
                                      {formatTimeOnly(a.checkedInAt)}
                                    </span>
                                  </div>
                                ))}
                                {attendees.length === 0 && (
                                  <p className="text-muted-foreground text-center py-4">
                                    아직 출석자가 없습니다
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Edit/Delete Actions */}
                          <div className="flex gap-2 pt-4 border-t">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => selectedEvent && startEditEvent(selectedEvent)}
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  수정
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>이벤트 수정</DialogTitle>
                                  <DialogDescription>
                                    이벤트 정보를 수정하세요.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="edit-title">제목 *</Label>
                                    <Input
                                      id="edit-title"
                                      value={formData.title}
                                      onChange={(e) =>
                                        setFormData({ ...formData, title: e.target.value })
                                      }
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="edit-description">설명</Label>
                                    <Textarea
                                      id="edit-description"
                                      value={formData.description}
                                      onChange={(e) =>
                                        setFormData({ ...formData, description: e.target.value })
                                      }
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="edit-location">장소</Label>
                                    <Input
                                      id="edit-location"
                                      value={formData.location}
                                      onChange={(e) =>
                                        setFormData({ ...formData, location: e.target.value })
                                      }
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label htmlFor="edit-startTime">시작 시간 *</Label>
                                      <Input
                                        id="edit-startTime"
                                        type="datetime-local"
                                        value={formData.startTime}
                                        onChange={(e) =>
                                          setFormData({ ...formData, startTime: e.target.value })
                                        }
                                      />
                                    </div>
                                    <div>
                                      <Label htmlFor="edit-endTime">종료 시간 *</Label>
                                      <Input
                                        id="edit-endTime"
                                        type="datetime-local"
                                        value={formData.endTime}
                                        onChange={(e) =>
                                          setFormData({ ...formData, endTime: e.target.value })
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <Label htmlFor="edit-points">포인트</Label>
                                    <Input
                                      id="edit-points"
                                      type="number"
                                      value={formData.points}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setFormData({ ...formData, points: parseInt(e.target.value) || 10 })
                                      }
                                    />
                                  </div>
                                  <Button
                                    className="w-full"
                                    onClick={updateEvent}
                                    disabled={isEditing || !formData.title || !formData.startTime || !formData.endTime}
                                  >
                                    {isEditing ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        수정 중...
                                      </>
                                    ) : (
                                      "수정 완료"
                                    )}
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => selectedEvent && deleteEvent(selectedEvent.id)}
                              disabled={isDeleting}
                            >
                              {isDeleting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  삭제
                                </>
                              )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
                {events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      아직 이벤트가 없습니다. 새 이벤트를 생성해보세요.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Superadmin Only: Deleted Users Management */}
        {userRole === "superadmin" && <DeletedUsersSection />}
      </main>
    </div>
  );
}
